import { create } from 'zustand';

import { getView, type EngineView, type ProfilerStatus } from '../../engine/prism.js';
import {
  FrameRing,
  exportCapture,
  importCapture,
  parseFrame,
  toCsv,
  type CaptureMeta,
  type ProfilerFrame,
} from '../../lib/profiler-model.js';
import { usePlay } from '../../state/play.js';

/**
 * Profiler session state shared by the Profiler panel: which view is
 * profiled, the recorder, the frames pulled from the engine (a
 * {@link FrameRing} outside React state; `version` bumps on change), the
 * selected frame / range and loaded captures. Recording keeps polling while
 * the panel is hidden.
 */

export type ProfilerTarget = 'auto' | 'play' | 'scene';

/** Frames the editor keeps (the engine keeps fewer and is drained every poll). */
export const EDITOR_CAPACITY = 2000;
const ENGINE_CAPACITY = 300;
const POLL_MS = 100;

export const frames = new FrameRing(EDITOR_CAPACITY);

interface ProfilerStore {
  target: ProfilerTarget;
  recording: boolean;
  gpu: boolean;
  /** Engine status of the profiled view (null: no view / older bundle). */
  status: ProfilerStatus | null;
  /** Which view the frames came from. */
  source: 'play' | 'scene' | null;
  /** Bumped whenever `frames` changes. */
  version: number;
  /** Selected frame (seq) — the timeline shows it. */
  selected: number | null;
  /** Selected range (seqs, inclusive) — the tables aggregate it. */
  range: [number, number] | null;
  /** Follow the newest frame while recording. */
  follow: boolean;
  /** Showing a loaded capture instead of live data. */
  loaded: CaptureMeta | null;
  error: string | null;
  setTarget(target: ProfilerTarget): void;
  setGpu(on: boolean): void;
  record(on: boolean): void;
  clear(): void;
  select(seq: number | null): void;
  setRange(range: [number, number] | null): void;
  setFollow(on: boolean): void;
  exportJson(): string;
  exportCsv(): string;
  load(text: string): void;
}

/** The view a target profiles: Play while it runs (auto), else the Scene view. */
export function resolveView(target: ProfilerTarget): { view: EngineView; mode: 'play' | 'scene' } | null {
  const playing = usePlay.getState().state !== 'stopped';
  const order: ('play' | 'scene')[] = target === 'play' ? ['play'] : target === 'scene' ? ['scene'] : playing ? ['play', 'scene'] : ['scene', 'play'];
  for (const mode of order) {
    const view = getView(mode);
    if (view) return { view, mode };
  }
  return null;
}

let timer: ReturnType<typeof setInterval> | null = null;
let current: EngineView | null = null;

function stopEngine(): void {
  if (current?.alive) void current.profiler({ record: false });
  current = null;
}

/** A poll still waiting on the engine (it may be busy loading in its worker). */
let polling = false;

function poll(): void {
  if (polling) return;
  polling = true;
  void pollOnce().finally(() => {
    polling = false;
  });
}

async function pollOnce(): Promise<void> {
  const s = useProfiler.getState();
  if (!s.recording) return;
  const found = resolveView(s.target);
  if (!found) {
    if (current) stopEngine();
    useProfiler.setState({ status: null, source: null, error: 'No Scene or Play view is open to profile.' });
    return;
  }
  const { view, mode } = found;
  if (!view.supportsProfiler) {
    useProfiler.setState({ status: null, source: mode, error: 'This engine build has no profiler — rebuild the engine (pnpm engine:build).' });
    return;
  }
  if (view !== current) {
    // A different view numbers its frames from its own counter.
    stopEngine();
    current = view;
    frames.clear();
    void view.profiler({ record: true, clear: true, gpu: s.gpu, capacity: ENGINE_CAPACITY });
    useProfiler.setState((st) => ({ version: st.version + 1, selected: null, range: null }));
  }
  const out = await view.profilerFrames(frames.lastSeq, ENGINE_CAPACITY);
  // Recording stopped or switched views meanwhile.
  if (!out || current !== view) return;
  const parsed = out.frames.map(parseFrame).filter((f): f is ProfilerFrame => f !== null);
  const added = frames.push(parsed);
  useProfiler.setState((st) => ({
    status: out.status,
    source: mode,
    error: null,
    ...(added ? { version: st.version + 1, selected: st.follow ? frames.lastSeq : st.selected } : {}),
  }));
}

function startPolling(): void {
  if (timer === null) timer = setInterval(poll, POLL_MS);
  poll();
}

function stopPolling(): void {
  if (timer !== null) clearInterval(timer);
  timer = null;
}

export const useProfiler = create<ProfilerStore>((set, get) => ({
  target: 'auto',
  recording: false,
  gpu: true,
  status: null,
  source: null,
  version: 0,
  selected: null,
  range: null,
  follow: true,
  loaded: null,
  error: null,
  setTarget(target) {
    set({ target });
    if (get().recording) poll();
  },
  setGpu(on) {
    set({ gpu: on });
    if (current?.alive) void current.profiler({ gpu: on }).then((status) => set({ status: status ?? get().status }));
  },
  record(on) {
    if (on) {
      if (get().loaded) {
        frames.clear();
        set((s) => ({ loaded: null, version: s.version + 1, selected: null, range: null }));
      }
      set({ recording: true, follow: true });
      startPolling();
    } else {
      poll();
      stopPolling();
      stopEngine();
      set({ recording: false, follow: false });
    }
  },
  clear() {
    frames.clear();
    if (current?.alive) void current.profiler({ clear: true });
    set((s) => ({ version: s.version + 1, selected: null, range: null, loaded: null }));
  },
  select(seq) {
    set({ selected: seq, follow: false });
  },
  setRange(range) {
    set({ range, follow: false });
  },
  setFollow(on) {
    set({ follow: on, ...(on ? { selected: frames.lastSeq >= 0 ? frames.lastSeq : null, range: null } : {}) });
  },
  exportJson() {
    const s = get();
    return exportCapture(frames.frames(), { target: s.loaded?.target ?? s.source ?? undefined, gpuSupported: s.status?.gpu.supported });
  },
  exportCsv() {
    const range = get().range;
    return toCsv(range ? frames.range(range[0], range[1]) : frames.frames());
  },
  load(text) {
    const capture = importCapture(text);
    if (get().recording) get().record(false);
    frames.clear();
    frames.capacity = Math.max(EDITOR_CAPACITY, capture.frames.length);
    frames.push(capture.frames);
    set((s) => ({
      loaded: capture.meta,
      version: s.version + 1,
      selected: capture.frames.length ? capture.frames[capture.frames.length - 1]!.seq : null,
      range: null,
      follow: false,
      error: null,
    }));
  },
}));
